import React, { useState, useEffect } from 'react';
import { api } from '../../services/apiClient';
import { theme } from '../../styles/theme';
import Button from '../ui/Button';
import Card from '../ui/Card';

const DashboardResale = ({ showSuccess, showError }) => {
  const [certificate, setCertificate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [file, setFile] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Don't fetch certificates on mount - removed API call
    setLoading(false);
  }, []);

  const fetchCertificate = async () => {
    // API call removed - certificates are no longer fetched automatically
    setLoading(false);
    setCertificate(null);
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!file) {
      setError('Please select a file to upload');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      // Step 1: Upload the file to Firebase Storage
      const uploadResponse = await api.resaleCertificates.uploadFile(file);
      const fileUrl = uploadResponse.url;
      
      // Step 2: Create the certificate record with the uploaded file URL
      const certData = {
        file_url: fileUrl,
        file_name: file.name,
        certificate_number: document.getElementById('cert-number')?.value || '',
        state: document.getElementById('cert-state')?.value || '',
        expiration_date: document.getElementById('cert-expiry')?.value || null,
      };

      const response = await api.resaleCertificates.create(certData);
      setCertificate(response);
      setFile(null);
      setError(null);
      
      // Show success message
      if (showSuccess) {
        showSuccess('Your tax exemption certificate has been uploaded successfully and is pending review.', 'Certificate Uploaded');
      }
      
      // Refresh the certificate list to ensure we have the latest
      await fetchCertificate();
    } catch (err) {
      const errorMsg = 'Failed to upload certificate: ' + (err.response?.data?.detail || err.message);
      setError(errorMsg);
      if (showError) {
        showError(errorMsg);
      }
    } finally {
      setLoading(false);
    }
  };

  const styles = {
    container: {
      width: '100%',
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: theme.spacing.xl,
      gap: theme.spacing.md,
      flexWrap: 'wrap',
    },
    title: {
      fontSize: theme.typography.fontSize['3xl'],
      fontFamily: theme.typography.fontFamily.primary,
      fontWeight: theme.typography.fontWeight.bold,
      color: theme.colors.text,
      margin: 0,
    },
    subtitle: {
      fontSize: theme.typography.fontSize.sm,
      color: theme.colors.textLight,
      margin: `${theme.spacing.xs} 0 0 0`,
      fontFamily: theme.typography.fontFamily.secondary,
    },
    formContainer: {
      maxWidth: '900px',
      margin: '0 auto',
    },
    formSection: {
      padding: theme.spacing.xl,
      borderBottom: `1px solid ${theme.colors.borderLight}`,
    },
    formSectionLast: {
      padding: theme.spacing.xl,
    },
    sectionHeader: {
      marginBottom: theme.spacing.lg,
    },
    sectionTitle: {
      fontSize: theme.typography.fontSize.lg,
      fontWeight: theme.typography.fontWeight.semibold,
      color: theme.colors.text,
      margin: 0,
      marginBottom: theme.spacing.xs,
      fontFamily: theme.typography.fontFamily.secondary,
    },
    sectionDescription: {
      fontSize: theme.typography.fontSize.sm,
      color: theme.colors.textLight,
      margin: 0,
      fontFamily: theme.typography.fontFamily.secondary,
    },
    formGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(2, 1fr)',
      gap: theme.spacing.lg,
    },
    formGroup: {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing.sm,
    },
    formGroupFull: {
      gridColumn: '1 / -1',
    },
    label: {
      fontSize: theme.typography.fontSize.sm,
      fontWeight: theme.typography.fontWeight.semibold,
      color: theme.colors.text,
      fontFamily: theme.typography.fontFamily.secondary,
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing.xs,
    },
    labelHint: {
      fontSize: theme.typography.fontSize.xs,
      fontWeight: theme.typography.fontWeight.normal,
      color: theme.colors.textLight,
      fontStyle: 'italic',
    },
    input: {
      padding: `${theme.spacing.md} ${theme.spacing.lg}`,
      fontSize: theme.typography.fontSize.base,
      fontFamily: theme.typography.fontFamily.secondary,
      border: `2px solid ${theme.colors.borderLight}`,
      borderRadius: theme.borderRadius.md,
      backgroundColor: theme.colors.surface,
      color: theme.colors.text,
      transition: `all ${theme.transitions.base}`,
      outline: 'none',
    },
    fileInput: {
      padding: `${theme.spacing.md}`,
      fontSize: theme.typography.fontSize.base,
      fontFamily: theme.typography.fontFamily.secondary,
      border: `2px dashed ${theme.colors.borderLight}`,
      borderRadius: theme.borderRadius.md,
      backgroundColor: '#FAFAFA',
      color: theme.colors.text,
      transition: `all ${theme.transitions.base}`,
      outline: 'none',
      cursor: 'pointer',
    },
    formActionsModern: {
      display: 'flex',
      gap: theme.spacing.md,
      justifyContent: 'flex-end',
      flexWrap: 'wrap',
      padding: theme.spacing.xl,
      backgroundColor: '#F9F9F9',
      borderTop: `2px solid ${theme.colors.borderLight}`,
    },
    certificateCard: {
      marginBottom: theme.spacing.xl,
    },
    certificateHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: theme.spacing.xl,
      paddingBottom: theme.spacing.lg,
      borderBottom: `2px solid ${theme.colors.borderLight}`,
    },
    certificateTitle: {
      fontSize: theme.typography.fontSize.xl,
      fontFamily: theme.typography.fontFamily.primary,
      fontWeight: theme.typography.fontWeight.bold,
      color: theme.colors.text,
      margin: 0,
    },
    statusBadge: {
      padding: `${theme.spacing.sm} ${theme.spacing.lg}`,
      borderRadius: theme.borderRadius.full,
      fontSize: theme.typography.fontSize.sm,
      fontWeight: theme.typography.fontWeight.bold,
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
    },
    statusApproved: {
      backgroundColor: '#D1FAE5',
      color: '#065F46',
      border: '2px solid #10B981',
    },
    statusPending: {
      backgroundColor: '#FEF3C7',
      color: '#92400E',
      border: '2px solid #F59E0B',
    },
    statusRejected: {
      backgroundColor: '#FEE2E2',
      color: '#991B1B',
      border: '2px solid #EF4444',
    },
    viewGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(2, 1fr)',
      gap: theme.spacing.xl,
    },
    viewField: {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing.sm,
    },
    viewLabel: {
      fontSize: theme.typography.fontSize.sm,
      fontWeight: theme.typography.fontWeight.semibold,
      color: theme.colors.textLight,
      fontFamily: theme.typography.fontFamily.secondary,
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
    },
    viewValue: {
      fontSize: theme.typography.fontSize.lg,
      fontWeight: theme.typography.fontWeight.medium,
      color: theme.colors.text,
      fontFamily: theme.typography.fontFamily.secondary,
      wordWrap: 'break-word',
      overflowWrap: 'break-word',
    },
    viewLink: {
      fontSize: theme.typography.fontSize.lg,
      fontWeight: theme.typography.fontWeight.medium,
      color: theme.colors.primary,
      fontFamily: theme.typography.fontFamily.secondary,
      textDecoration: 'none',
      display: 'inline-flex',
      alignItems: 'center',
      gap: theme.spacing.sm,
      transition: `all ${theme.transitions.base}`,
    },
    emptyState: {
      textAlign: 'center',
      padding: `${theme.spacing.xxl} ${theme.spacing.xl}`,
      backgroundColor: theme.colors.surface,
      borderRadius: theme.borderRadius.lg,
      border: `2px dashed ${theme.colors.borderLight}`,
      marginBottom: theme.spacing.xl,
    },
    emptyStateIcon: {
      fontSize: '3rem',
      marginBottom: theme.spacing.lg,
    },
    emptyStateTitle: {
      fontSize: theme.typography.fontSize['2xl'],
      fontFamily: theme.typography.fontFamily.primary,
      fontWeight: theme.typography.fontWeight.semibold,
      color: theme.colors.text,
      marginBottom: theme.spacing.md,
    },
    emptyStateText: {
      fontSize: theme.typography.fontSize.base,
      color: theme.colors.textLight,
      marginBottom: theme.spacing.sm,
      lineHeight: theme.typography.lineHeight.relaxed,
    },
    errorMessage: {
      padding: theme.spacing.lg,
      backgroundColor: '#FEE2E2',
      border: `2px solid #EF4444`,
      borderRadius: theme.borderRadius.md,
      color: '#991B1B',
      marginBottom: theme.spacing.lg,
      fontSize: theme.typography.fontSize.sm,
      fontWeight: theme.typography.fontWeight.medium,
    },
    infoBanner: {
      backgroundColor: '#EFF6FF',
      border: `2px solid #3B82F6`,
      borderRadius: theme.borderRadius.lg,
      padding: theme.spacing.lg,
      marginBottom: theme.spacing.xl,
      display: 'flex',
      gap: theme.spacing.md,
      alignItems: 'flex-start',
    },
    infoBannerIcon: {
      fontSize: '1.5rem',
      color: '#3B82F6',
      flexShrink: 0,
    },
    infoBannerContent: {
      flex: 1,
    },
    infoBannerTitle: {
      fontSize: theme.typography.fontSize.base,
      fontWeight: theme.typography.fontWeight.semibold,
      color: '#1E40AF',
      margin: `0 0 ${theme.spacing.xs} 0`,
      fontFamily: theme.typography.fontFamily.secondary,
    },
    infoBannerText: {
      fontSize: theme.typography.fontSize.sm,
      color: '#1E40AF',
      margin: 0,
      fontFamily: theme.typography.fontFamily.secondary,
      lineHeight: theme.typography.lineHeight.relaxed,
    },
  };

  const responsiveStyles = `
    .resale-form-grid {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: ${theme.spacing.lg};
    }

    .resale-form-full {
      grid-column: 1 / -1;
    }

    .resale-input:focus {
      border-color: ${theme.colors.primary};
      box-shadow: 0 0 0 4px rgba(204, 85, 0, 0.1);
      transform: translateY(-1px);
    }

    .resale-file-input:focus,
    .resale-file-input:hover {
      border-color: ${theme.colors.primary};
      background-color: #FFF8F0;
    }

    .resale-view-link:hover {
      color: ${theme.colors.primaryDark};
      text-decoration: underline;
    }

    .resale-view-grid {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: ${theme.spacing.xl};
    }

    @media (max-width: 768px) {
      .resale-form-grid,
      .resale-view-grid {
        grid-template-columns: 1fr;
        gap: ${theme.spacing.md};
      }

      .resale-header {
        flex-direction: column;
        align-items: flex-start;
        gap: ${theme.spacing.md};
      }

      .resale-header h2 {
        font-size: ${theme.typography.fontSize['2xl']};
      }

      .form-section,
      .form-section-last,
      .form-actions-modern {
        padding: ${theme.spacing.lg};
      }

      .certificate-header {
        flex-direction: column;
        align-items: flex-start;
        gap: ${theme.spacing.md};
      }
    }

    @media (max-width: 480px) {
      .resale-form-actions {
        flex-direction: column-reverse;
      }

      .resale-form-actions button {
        width: 100%;
      }

      .form-section,
      .form-section-last {
        padding: ${theme.spacing.md};
      }

      .form-actions-modern {
        padding: ${theme.spacing.md};
      }
    }
  `;

  const getStatusStyle = (status) => {
    const statusLower = status?.toLowerCase() || 'pending';
    if (statusLower === 'approved') return styles.statusApproved;
    if (statusLower === 'rejected') return styles.statusRejected;
    return styles.statusPending;
  };

  if (loading && !certificate) {
    return (
      <>
        <style>{responsiveStyles}</style>
        <div style={styles.container}>
          <div style={{ textAlign: 'center', padding: theme.spacing.xxl }}>
            <p style={{ fontSize: theme.typography.fontSize.lg, color: theme.colors.textLight }}>
              Loading certificate information...
            </p>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <style>{responsiveStyles}</style>
      <div style={styles.container}>
        <div style={styles.header} className="resale-header">
          <div>
            <h2 style={styles.title}>Tax Exemption Certificate</h2>
            <p style={styles.subtitle}>
              Upload your resale certificate to qualify for tax-exempt purchases
            </p>
            <p style={{...styles.subtitle, marginTop: theme.spacing.xs, fontSize: theme.typography.fontSize.sm, fontStyle: 'italic'}}>
              If you've already uploaded a certificate, there is no need to upload again unless you need to update it.
            </p>
          </div>
        </div>

        {certificate && (
          <Card variant="parchment" padding="xl" style={styles.certificateCard}>
            <div style={styles.certificateHeader} className="certificate-header">
              <h3 style={styles.certificateTitle}>Current Certificate</h3>
              <div style={{...styles.statusBadge, ...getStatusStyle(certificate.status)}}>
                {certificate.status || 'Pending'}
              </div>
            </div>
            
            <div className="resale-view-grid" style={styles.viewGrid}>
              <div style={styles.viewField}>
                <div style={styles.viewLabel}>Certificate Number</div>
                <div style={styles.viewValue}>{certificate.certificate_number || 'N/A'}</div>
              </div>

              <div style={styles.viewField}>
                <div style={styles.viewLabel}>State</div>
                <div style={styles.viewValue}>{certificate.state || 'N/A'}</div>
              </div>

              <div style={styles.viewField}>
                <div style={styles.viewLabel}>Expiration Date</div>
                <div style={styles.viewValue}>
                  {certificate.expiration_date 
                    ? new Date(certificate.expiration_date).toLocaleDateString('en-US', { 
                        year: 'numeric', 
                        month: 'long', 
                        day: 'numeric' 
                      })
                    : 'No expiration'}
                </div>
              </div>

              <div style={styles.viewField}>
                <div style={styles.viewLabel}>Uploaded On</div>
                <div style={styles.viewValue}>
                  {new Date(certificate.created_at).toLocaleDateString('en-US', { 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })}
                </div>
              </div>

              {certificate.file_url && (
                <div style={{...styles.viewField, ...styles.formGroupFull}}>
                  <div style={styles.viewLabel}>Certificate File</div>
                  <div style={{
                    padding: theme.spacing.md,
                    backgroundColor: '#F0F9FF',
                    border: '1px solid #BAE6FD',
                    borderRadius: theme.borderRadius.md,
                    color: '#0369A1',
                    fontSize: theme.typography.fontSize.sm,
                    display: 'flex',
                    alignItems: 'center',
                    gap: theme.spacing.sm,
                  }}>
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M10 0C4.5 0 0 4.5 0 10C0 15.5 4.5 20 10 20C15.5 20 20 15.5 20 10C20 4.5 15.5 0 10 0ZM10 18C5.6 18 2 14.4 2 10C2 5.6 5.6 2 10 2C14.4 2 18 5.6 18 10C18 14.4 14.4 18 10 18Z" fill="currentColor"/>
                      <path d="M9 5H11V11H9V5Z" fill="currentColor"/>
                      <path d="M9 13H11V15H9V13Z" fill="currentColor"/>
                    </svg>
                    <span>
                      Certificate uploaded successfully. Only administrators can view certificate files for security and privacy purposes.
                    </span>
                  </div>
                </div>
              )}
            </div>
          </Card>
        )}


        <div style={styles.formContainer}>
          <Card variant="parchment" padding="none">
            <form onSubmit={handleUpload}>
              {/* Certificate Details Section */}
              <div style={styles.formSection}>
                <div style={styles.sectionHeader}>
                  <h3 style={styles.sectionTitle}>Certificate Details</h3>
                  <p style={styles.sectionDescription}>Enter your resale certificate information</p>
                </div>
                
                {error && <div style={styles.errorMessage}>{error}</div>}

                <div className="resale-form-grid">
                  <div className="form-group">
                    <label style={styles.label}>
                      Certificate Number *
                      <span style={styles.labelHint}>As shown on your certificate</span>
                    </label>
                    <input 
                      id="cert-number" 
                      type="text" 
                      placeholder="e.g., 12-3456789" 
                      style={styles.input}
                      className="resale-input"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label style={styles.label}>
                      State *
                      <span style={styles.labelHint}>State that issued the certificate</span>
                    </label>
                    <input 
                      id="cert-state" 
                      type="text" 
                      placeholder="e.g., TX, CA, NY" 
                      style={styles.input}
                      className="resale-input"
                      required
                    />
                  </div>

                  <div className="form-group resale-form-full">
                    <label style={styles.label}>
                      Expiration Date
                      <span style={styles.labelHint}>Optional - Leave blank if no expiration</span>
                    </label>
                    <input 
                      id="cert-expiry" 
                      type="date" 
                      style={styles.input}
                      className="resale-input"
                    />
                  </div>
                </div>
              </div>

              {/* File Upload Section */}
              <div style={styles.formSectionLast}>
                <div style={styles.sectionHeader}>
                  <h3 style={styles.sectionTitle}>Upload Certificate</h3>
                  <p style={styles.sectionDescription}>Upload a clear copy of your resale certificate</p>
                </div>

                <div className="form-group">
                  <label style={styles.label}>
                    Certificate File *
                    <span style={styles.labelHint}>PDF, JPG, or PNG format (max 10MB)</span>
                  </label>
                  <input 
                    type="file" 
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => setFile(e.target.files[0])}
                    required
                    style={styles.fileInput}
                    className="resale-file-input"
                  />
                  {file && (
                    <div style={{ 
                      marginTop: theme.spacing.md, 
                      padding: theme.spacing.md,
                      backgroundColor: '#F0FDF4',
                      border: '2px solid #10B981',
                      borderRadius: theme.borderRadius.md,
                      fontSize: theme.typography.fontSize.sm,
                      color: '#065F46',
                      fontWeight: theme.typography.fontWeight.medium,
                    }}>
                      ✓ Selected: {file.name}
                    </div>
                  )}
                </div>
              </div>

              {/* Form Actions */}
              <div style={styles.formActionsModern} className="resale-form-actions">
                <Button 
                  type="submit" 
                  disabled={loading || !file}
                  size="lg"
                >
                  {loading ? 'Uploading...' : 'Upload Certificate'}
                </Button>
              </div>
            </form>
          </Card>
        </div>
      </div>
    </>
  );
};

export default DashboardResale;
